// @ts-nocheck
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2, RefreshCw } from 'lucide-react';
import { getDivisionColor } from '@/utils/divisionColors';
import { PublicEventHeader } from '@/components/public/PublicEventHeader';
import { usePublicLive, useDivisionParam, formatUpdated } from '@/hooks/usePublicLive';
import PublicDivisionBracket from './PublicDivisionBracket';

const FN = 'rle-public-schedule';
const NAVY = '#0a1f44';

const PublicBracketsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [division, setDivision] = useDivisionParam();

  const { data, loading, errorCode, lastUpdated, refreshing, refresh } = usePublicLive(
    FN,
    { action: 'brackets', slug },
    { enabled: !!slug },
  );

  const divisionOptions = useMemo(() => data?.all_divisions || [], [data]);
  const visible = useMemo(() => {
    const list = data?.divisions || [];
    return division === 'all' ? list : list.filter((d: any) => d.division === division);
  }, [data, division]);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (errorCode === 'NOT_PUBLISHED') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">
          This schedule hasn't been published yet.
        </h1>
      </div>
    );
  }

  if (errorCode === 'NOT_FOUND') {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">Event not found.</h1>
      </div>
    );
  }

  if (errorCode || !data) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="text-2xl font-bold text-foreground">Could not load brackets.</h1>
      </div>
    );
  }

  const event = data.event || {};
  const divColor = (d?: string | null) => getDivisionColor(d, data?.division_colors?.[d as string] ?? null);

  return (
    <div className="mx-auto max-w-[1600px] px-4 py-8">
      <PublicEventHeader
        slug={slug ?? ''} current="brackets" division={division}
        event={event} branding={data.branding} tabs={data.tabs || []} teams={[]}
      />

      <div className="mb-8 flex flex-wrap items-end gap-3">
        {divisionOptions.length > 0 && (
          <div className="flex flex-col gap-1">
            <label className="text-xs font-medium text-muted-foreground">Division</label>
            <Select value={division} onValueChange={setDivision}>
              <SelectTrigger className="w-56">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All divisions</SelectItem>
                {divisionOptions.map((d: string) => {
                  const c = divColor(d);
                  return (
                    <SelectItem key={d} value={d}>
                      <span className="flex items-center gap-2">
                        <span
                          className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: c.dot }}
                        />
                        {d}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
        )}
        <Button variant="ghost" size="sm" onClick={refresh} disabled={refreshing}>
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
        <span className="pb-2 text-xs text-muted-foreground">
          Updated {formatUpdated(lastUpdated)}
        </span>
      </div>

      {visible.length === 0 ? (
        <div className="rounded-lg border bg-muted/30 py-16 text-center">
          <p className="text-lg font-semibold text-foreground">
            Brackets have not been posted yet.
          </p>
          <p className="mt-2 text-sm text-muted-foreground">
            They appear here as soon as bracket games are published.
          </p>
        </div>
      ) : (
        visible.map((d: any) => {
          const c = divColor(d.division);
          const splitRounds = (consolation: boolean) => (d.rounds || []).map((r: any) => ({
            ...r, games: r.games.filter((g: any) => (g.game_type === 'consolation') === consolation),
          })).filter((r: any) => r.games.length);
          const mainRounds = splitRounds(false);
          const consolationRounds = splitRounds(true);
          return (
            <section key={d.division} className="mb-10">
              <h2
                className="mb-4 inline-flex items-center gap-2 border-l-4 pl-3 text-xl font-bold"
                style={{ color: NAVY, borderColor: c.border }}
              >
                {d.division}
              </h2>
              {/* The organiser's own words about each bracket in this division. */}
              {(d.brackets || []).map((b: any) => (
                <div key={b.id} className="mb-3 max-w-2xl">
                  <p className="text-sm font-semibold text-foreground">{b.name}</p>
                  <p className="whitespace-pre-wrap text-sm text-muted-foreground">{b.description}</p>
                </div>
              ))}
              <PublicDivisionBracket rounds={mainRounds} slug={slug ?? ''} borderColor={c.border} />
              {consolationRounds.length > 0 && (
                <div className="mt-8">
                  <h3 className="mb-4 text-lg font-bold text-foreground">Consolation</h3>
                  <PublicDivisionBracket rounds={consolationRounds} slug={slug ?? ''} borderColor={c.border} consolation />
                </div>
              )}
            </section>
          );
        })
      )}
    </div>
  );
};

export default PublicBracketsPage;
